/**
 * Parametres : equipe, cles API, webhooks.
 *
 * Le point sensible est la CLE API. Elle n'est affichee qu'UNE FOIS, et
 * c'est la premiere cause de ticket support sur ce type de produit. Le
 * traitement visuel doit donc le dire fort - un simple texte gris ne
 * suffit pas, l'utilisateur ferme la fenetre sans le lire.
 */
import { useCallback, useEffect, useState } from "react";

import { api, liste, type CleApi, type Membre, type Moi, type ParametresClient, type Reglage, type Webhook } from "../api";
import { Chargement, Erreur, Vide, date } from "../components/ui";
import { ConservationProuver } from "./ConservationProuver";
import { ExpediteursProuver } from "./ExpediteursProuver";
import { DoubleAuthentification } from "./DoubleAuthentification";
import { FicheClient } from "./FicheClient";
import { IdentiteDuCompte, NomDuCompte } from "./NomDuCompte";

const ROLES = [
  { code: "admin", libelle: "Administrateur — tout, y compris les clés API" },
  { code: "manager", libelle: "Gestionnaire — crée et suit les enveloppes" },
  { code: "billing", libelle: "Facturation — crédit et factures" },
  { code: "developer", libelle: "Développeur — intégration technique" },
  { code: "security", libelle: "Sécurité — consultation et audit" },
  { code: "viewer", libelle: "Lecture seule" },
];

const SCOPES = [
  "view_documents", "manage_documents", "view_envelopes", "manage_envelopes",
  "view_webhooks", "manage_webhooks", "view_billing",
];

/** Un evenement par produit, avec toutes les informations dedans (Franck,
 *  08/10/2026) : resultat, motif, signataires, documents, dossier de preuve,
 *  montant... Le serveur refuse tout autre nom. */
const EVENEMENTS_PAR_PRODUIT: { produit: string; evenements: { code: string; libelle: string }[] }[] = [
  { produit: "Signer", evenements: [{ code: "signer.termine",
    libelle: "Résultat de l'enveloppe : signée, refusée, annulée, périmée ou en échec (signataires, documents, dossier de preuve)" },
  { code: "signer.demande",
    libelle: "Demande d'un signataire (transmission, correction du nom) et votre réponse" }] },
  { produit: "Prouver", evenements: [{ code: "prouver.termine",
    libelle: "Journal de preuve constitué (statut, réponses SMTP, pièces, montant)" }] },
  { produit: "Sécuriser", evenements: [{ code: "securiser.termine",
    libelle: "Document scellé (empreintes, horodateur, montant)" }] },
];
const LIBELLES_EVENEMENTS = Object.fromEntries(
  EVENEMENTS_PAR_PRODUIT.flatMap((p) => p.evenements.map((e) => [e.code, `${p.produit} · ${e.libelle}`])));

export function Settings() {
  const [membres, setMembres] = useState<Membre[]>([]);
  const [cles, setCles] = useState<CleApi[]>([]);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [erreur, setErreur] = useState<unknown>(null);
  const [chargement, setChargement] = useState(true);

  const [emailInvite, setEmailInvite] = useState("");
  const [roleInvite, setRoleInvite] = useState("manager");
  const [inviteEnvoyee, setInviteEnvoyee] = useState(false);

  const [nomCle, setNomCle] = useState("");
  const [scopesCle, setScopesCle] = useState<string[]>(["view_envelopes", "manage_envelopes"]);
  /** Valeur en clair de la cle qui vient d'etre creee. Vit UNIQUEMENT en
   *  memoire, le temps de l'affichage : elle n'est jamais relisible
   *  ensuite, ni cote serveur ni ici. */
  const [cleEnClair, setCleEnClair] = useState<string | null>(null);

  const [nomWebhook, setNomWebhook] = useState("");
  const [urlWebhook, setUrlWebhook] = useState("");
  const [evenementsChoisis, setEvenementsChoisis] = useState<string[]>(
    EVENEMENTS_PAR_PRODUIT.flatMap((p) => p.evenements.map((e) => e.code)));
  const [secretWebhook, setSecretWebhook] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const [m, c, w] = await Promise.all([
        api.membres().catch(() => []),
        api.clesApi().catch(() => []),
        api.webhooks().catch(() => []),
      ]);
      setMembres(liste<Membre>(m, "members"));
      setCles(liste<CleApi>(c, "api_keys"));
      setWebhooks(liste<Webhook>(w, "endpoints"));
    } catch (e) {
      setErreur(e);
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (chargement) return <Chargement />;

  const agir = async (fn: () => Promise<unknown>) => {
    setErreur(null);
    try {
      await fn();
      await charger();
    } catch (e) {
      setErreur(e);
    }
  };

  return (
    <>
      <h1 className="page-title">Paramètres</h1>
      <p className="page-lead">Liens de signature, Prouver, équipe, clés API et webhooks.</p>

      <Erreur erreur={erreur} />

      <IdentiteDuCompte />
      <NomDuCompte />
      <FicheClient />
      <DoubleAuthentification />
      <DureeDesLiens />
      <ValeursParDefaut />
      <MessageParDefaut />
      <ReglagesSignataires />
      <AvisEtErreurs />
      <ExpediteursProuver />
      <ConservationProuver />

      {/* ---------------------------------------------- equipe */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Équipe
        </h2>
        {membres.length === 0 ? (
          <Vide>Aucun membre listé.</Vide>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Membre</th>
                  <th>Rôle</th>
                </tr>
              </thead>
              <tbody>
                {membres.map((m) => (
                  <tr key={m.user_id}>
                    <td>
                      {m.full_name || m.email}
                      {m.full_name && <div className="muted">{m.email}</div>}
                    </td>
                    <td>{m.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="field">
            <label className="field-label" htmlFor="invite">
              Inviter par e-mail
            </label>
            <input
              id="invite"
              className="input"
              type="email"
              value={emailInvite}
              onChange={(e) => setEmailInvite(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="role">
              Rôle
            </label>
            <select
              id="role"
              className="select"
              value={roleInvite}
              onChange={(e) => setRoleInvite(e.target.value)}
            >
              {ROLES.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.libelle}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!emailInvite.includes("@")}
            onClick={() =>
              void agir(async () => {
                await api.inviter(emailInvite, roleInvite);
                setEmailInvite("");
                setInviteEnvoyee(true);
              })
            }
          >
            Envoyer l'invitation
          </button>
          {inviteEnvoyee && <span className="muted">Invitation envoyée.</span>}
        </div>
      </div>

      {/* --------------------------------------------- cles API */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Clés API
        </h2>

        {cleEnClair && (
          <div className="secret-once">
            <strong>⚠️ Copiez cette clé maintenant — elle ne sera plus jamais affichée.</strong>
            <code>{cleEnClair}</code>
            <p style={{ margin: 0 }}>
              Seule son empreinte est conservée : ni vous ni nous ne pourrons la retrouver. Si
              vous la perdez, il faudra en créer une nouvelle et révoquer celle-ci.
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => void navigator.clipboard?.writeText(cleEnClair)}
              >
                Copier
              </button>
              <button
                type="button"
                className="btn btn--link"
                onClick={() => setCleEnClair(null)}
              >
                J'ai copié la clé
              </button>
            </div>
          </div>
        )}

        {cles.length === 0 ? (
          <Vide>Aucune clé API.</Vide>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Droits</th>
                  <th>Créée le</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cles.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td className="muted">{(c.scopes ?? []).join(", ")}</td>
                    <td>{date(c.created_at)}</td>
                    <td className="num">
                      {c.revoked_at ? (
                        <span className="pill pill--muted">Révoquée</span>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--link"
                          onClick={() => {
                            if (window.confirm(`Révoquer définitivement la clé « ${c.name} » ?`)) {
                              void agir(() => api.revoquerCleApi(c.id));
                            }
                          }}
                        >
                          Révoquer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="field">
            <label className="field-label" htmlFor="nomcle">
              Nom de la clé
            </label>
            <input
              id="nomcle"
              className="input"
              value={nomCle}
              onChange={(e) => setNomCle(e.target.value)}
              placeholder="Intégration CRM"
            />
          </div>
        </div>
        <fieldset style={{ border: "none", padding: 0, margin: "0 0 1rem" }}>
          <legend className="field-label">Droits accordés</legend>
          <div className="row">
            {SCOPES.map((s) => (
              <label key={s} style={{ display: "flex", gap: "0.4rem", alignItems: "center", minWidth: "13rem" }}>
                <input
                  type="checkbox"
                  checked={scopesCle.includes(s)}
                  onChange={(e) =>
                    setScopesCle((v) => (e.target.checked ? [...v, s] : v.filter((x) => x !== s)))
                  }
                />
                <span className="muted">{s}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!nomCle.trim() || scopesCle.length === 0}
            onClick={() =>
              void agir(async () => {
                const creee = await api.creerCleApi(nomCle.trim(), scopesCle);
                if (creee.key) setCleEnClair(creee.key);
                setNomCle("");
              })
            }
          >
            Créer une clé
          </button>
        </div>
      </div>

      {/* --------------------------------------------- webhooks */}
      <div className="card">
        <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
          Webhooks
        </h2>
        <p className="card__lead">
          Chaque envoi est signé en HMAC-SHA256 et réessayé avec un délai croissant en cas
          d'échec : une indisponibilité passagère de votre côté ne perd aucun événement.
        </p>

        {secretWebhook && (
          <div className="secret-once">
            <strong>⚠️ Secret de signature — affiché une seule fois.</strong>
            <code>{secretWebhook}</code>
            <p style={{ margin: 0 }}>
              Il sert à vérifier que les appels reçus viennent bien de nous.
            </p>
            <div className="actions">
              <button type="button" className="btn btn--link" onClick={() => setSecretWebhook(null)}>
                J'ai copié le secret
              </button>
            </div>
          </div>
        )}

        {webhooks.length === 0 ? (
          <Vide>Aucun webhook configuré.</Vide>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>URL</th>
                  <th>Événements</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {webhooks.map((w) => (
                  <tr key={w.id}>
                    <td>{w.name}</td>
                    <td className="muted">{w.target_url}</td>
                    <td className="muted">{(w.subscribed_events ?? []).map((e) => LIBELLES_EVENEMENTS[e] ?? e).join(", ")}</td>
                    <td className="num">
                      <button
                        type="button"
                        className="btn btn--link"
                        onClick={() => void agir(() => api.supprimerWebhook(w.id))}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="row" style={{ marginTop: "1rem" }}>
          <div className="field">
            <label className="field-label" htmlFor="nomwh">
              Nom
            </label>
            <input
              id="nomwh"
              className="input"
              value={nomWebhook}
              onChange={(e) => setNomWebhook(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="urlwh">
              URL de destination (https)
            </label>
            <input
              id="urlwh"
              className="input"
              value={urlWebhook}
              onChange={(e) => setUrlWebhook(e.target.value)}
              placeholder="https://exemple.com/sign365"
            />
          </div>
        </div>
        <fieldset className="field" style={{ border: 0, padding: 0, margin: "0.5rem 0" }}>
          <legend className="field-label">Événements envoyés à cette adresse</legend>
          {EVENEMENTS_PAR_PRODUIT.map((p) => (
            <div key={p.produit} style={{ marginBottom: "0.4rem" }}>
              <strong>{p.produit}</strong>
              {p.evenements.map((e) => (
                <label key={e.code} style={{ display: "block" }}>
                  <input type="checkbox" checked={evenementsChoisis.includes(e.code)}
                         onChange={(ev) => setEvenementsChoisis((v) => ev.target.checked
                           ? [...v, e.code] : v.filter((x) => x !== e.code))} />{" "}
                  {e.libelle}
                </label>
              ))}
            </div>
          ))}
        </fieldset>
        <div className="actions" style={{ marginTop: 0 }}>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!nomWebhook.trim() || !urlWebhook.startsWith("https://") || evenementsChoisis.length === 0}
            onClick={() =>
              void agir(async () => {
                const cree = await api.creerWebhook(nomWebhook.trim(), urlWebhook.trim(), evenementsChoisis);
                if (cree.secret) setSecretWebhook(cree.secret);
                setNomWebhook("");
                setUrlWebhook("");
              })
            }
          >
            Ajouter un webhook
          </button>
        </div>
      </div>
    </>
  );
}

/** T-32 : duree de vie des liens de signature de CE client. Vide = il suit
 *  la valeur de la plateforme (et la suivra si elle change). Modifiable par
 *  les roles qui envoient des enveloppes ; les autres voient la valeur. */
type Defaut = "signing_order_imposed" | "remplissage_ordre_impose" | "auth_method_defaut" | "langue_defaut";

/** Lot 2quater (Franck, 19/10/2026) : valeurs par defaut de chaque nouvelle
 *  enveloppe - le formulaire de l'app part d'elles, l'API les reprend quand
 *  la requete ne les donne pas. Remplace la carte « Ordre des signataires »
 *  (B-07). */
const DEFAUTS: { cle: Defaut; titre: string; aide: string;
                 choix: { valeur: boolean | string; libelle: string; aide: string }[] }[] = [
  { cle: "signing_order_imposed", titre: "Ordre de signature", aide: "Champ API : signing_order_imposed.",
    choix: [{ valeur: true, libelle: "Imposé", aide: "Chaque signataire reçoit son lien quand le précédent a signé." },
            { valeur: false, libelle: "Libre", aide: "Tous reçoivent leur lien en même temps." }] },
  { cle: "remplissage_ordre_impose", titre: "Ordre de remplissage (traitement avant signature)",
    aide: "Champ API : remplissage_ordre_impose.",
    choix: [{ valeur: true, libelle: "Imposé", aide: "Chaque remplisseur complète quand le précédent a fini." },
            { valeur: false, libelle: "Libre", aide: "Tous les remplisseurs complètent en même temps." }] },
  { cle: "auth_method_defaut", titre: "Authentification des signataires", aide: "Champ API : auth_method.",
    choix: [{ valeur: "otp_email", libelle: "Code par e-mail", aide: "Le signataire reçoit un code à son adresse." },
            { valeur: "otp_sms", libelle: "Code par SMS",
              aide: "Le signataire reçoit un code sur son mobile : il faut alors son numéro." }] },
  { cle: "langue_defaut", titre: "Langue des signataires", aide: "Champ API : langue.",
    choix: [{ valeur: "fr", libelle: "Français", aide: "Parcours, e-mails et Journal de preuve en français." },
            { valeur: "en", libelle: "English", aide: "Parcours, e-mails et Journal de preuve en anglais." }] },
];

function ValeursParDefaut() {
  const [parametres, setParametres] = useState<ParametresClient | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.parametresClient().then(setParametres).catch(setErreur);
  }, []);

  if (!parametres) return <Erreur erreur={erreur} />;

  const choisir = async (cle: Defaut, valeur: boolean | string) => {
    if (parametres[cle] === valeur) return;
    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      setParametres(await api.definirParametres({ [cle]: valeur }));
      setMessage("Réglage enregistré : il vaut pour vos prochaines enveloppes.");
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Valeurs par défaut des enveloppes</h2>
      <p>Chaque nouvelle enveloppe part de ces valeurs : vous pouvez les changer au moment de la créer.
        Une enveloppe créée par l'API les reprend quand sa requête ne les donne pas.</p>
      <div className="reglages">
        {DEFAUTS.map((d) => (
          <fieldset key={d.cle} className="reglage" disabled={enCours}>
            <legend>{d.titre}</legend>
            <p className="muted" style={{ margin: "0 0 0.25rem" }}>{d.aide}</p>
            {d.choix.map((c) => (
              <label key={String(c.valeur)} className="reglage__choix">
                <input type="radio" name={d.cle} checked={parametres[d.cle] === c.valeur}
                       onChange={() => void choisir(d.cle, c.valeur)} />
                <span><strong>{c.libelle}</strong><small>{c.aide}</small></span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      {message && <div className="alert alert--success" style={{ marginTop: "0.75rem" }}>{message}</div>}
      <Erreur erreur={erreur} />
    </div>
  );
}

const ADRESSE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Lot 2quater (Franck, 19/10/2026) : « E-mail des avis et des erreurs »,
 *  pour les enveloppes creees par l'API. Proprietaire ou administrateur. */
function AvisEtErreurs() {
  const [parametres, setParametres] = useState<ParametresClient | null>(null);
  const [moi, setMoi] = useState<Moi | null>(null);
  const [saisie, setSaisie] = useState("");
  const [erreur, setErreur] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.parametresClient().then((p) => { setParametres(p); setSaisie(p.email_avis.join("\n")); }).catch(setErreur);
    api.moi().then(setMoi).catch(() => undefined);
  }, []);

  if (!parametres) return <Erreur erreur={erreur} />;
  const peutModifier = moi?.role === "owner" || moi?.role === "admin";
  const adresses = saisie.split(/[\s,;]+/).filter(Boolean);
  const invalides = adresses.filter((a) => !ADRESSE.test(a));
  const tropNombreuses = adresses.length > 10;

  const enregistrer = async () => {
    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      const p = await api.definirParametres({ email_avis: adresses });
      setParametres(p);
      setSaisie(p.email_avis.join("\n"));
      setMessage(p.email_avis.length ? "Adresses enregistrées." : "Aucune adresse : les avis passent par vos webhooks.");
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Avis et erreurs des enveloppes créées par l'API</h2>
      <p>Ces adresses reçoivent les avis (un signataire transmet sa signature, demande une transmission ou la
        correction de son nom) et les erreurs (signature en échec, invitation non remise, webhook abandonné).
        Le champ <code>email_avis</code> d'une requête les remplace pour son enveloppe. Les enveloppes créées
        ici préviennent la personne qui les a créées.</p>
      <p className="muted">Sans adresse ici ni dans la requête, une enveloppe dont un signataire peut transmettre
        ou demander est refusée, sauf si un webhook actif est abonné à « signer.demande ».</p>
      {!peutModifier && moi && (
        <p className="muted">Réservé au propriétaire ou à un administrateur du compte.</p>
      )}
      <div className="field">
        <label className="field-label" htmlFor="email-avis">E-mail des avis et des erreurs (une adresse par ligne, 10 au plus)</label>
        <textarea id="email-avis" className="input" rows={3} value={saisie} disabled={!peutModifier || enCours}
                  placeholder="contrats@exemple.fr"
                  aria-invalid={invalides.length > 0 || tropNombreuses}
                  onChange={(e) => { setSaisie(e.target.value); setMessage(null); }} />
      </div>
      {(invalides.length > 0 || tropNombreuses) && (
        <div className="alert alert--error" role="alert" style={{ marginTop: "0.5rem" }}>
          {invalides.length > 0 ? `Adresse invalide : ${invalides.join(", ")}.` : "10 adresses au plus."}
        </div>
      )}
      <div className="actions">
        <button type="button" className="btn btn--primary" onClick={() => void enregistrer()}
                disabled={!peutModifier || enCours || invalides.length > 0 || tropNombreuses}>
          Enregistrer
        </button>
      </div>
      {message && <div className="alert alert--success" style={{ marginTop: "0.75rem" }}>{message}</div>}
      <Erreur erreur={erreur} />
    </div>
  );
}

const CHOIX: { valeur: Reglage; libelle: string; aide: string }[] = [
  { valeur: "autoriser", libelle: "Autoriser", aide: "Le signataire le fait lui-même, tout de suite." },
  { valeur: "demander", libelle: "Sur demande", aide: "Le signataire le demande ; vous acceptez ou refusez, la signature attend votre réponse." },
  { valeur: "interdire", libelle: "Interdire", aide: "Impossible : le signataire est invité à vous contacter." },
];

/** Lot 2bis (Franck, 15/10/2026) : deux reglages du compte, a trois valeurs.
 *  Modifiables par le proprietaire ou un administrateur ; les autres voient. */
function ReglagesSignataires() {
  const [parametres, setParametres] = useState<ParametresClient | null>(null);
  const [moi, setMoi] = useState<Moi | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.parametresClient().then(setParametres).catch(setErreur);
    api.moi().then(setMoi).catch(() => undefined);
  }, []);

  if (!parametres) return <Erreur erreur={erreur} />;
  const peutModifier = moi?.role === "owner" || moi?.role === "admin";

  const choisir = async (reglage: "transmission_defaut" | "correction_nom_defaut", valeur: Reglage) => {
    if (parametres[reglage] === valeur) return;
    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      setParametres(await api.definirReglageClient(reglage, valeur));
      setMessage("Réglage enregistré : il vaut pour vos prochaines enveloppes.");
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  };

  const groupe = (reglage: "transmission_defaut" | "correction_nom_defaut", titre: string, aide: string) => (
    <fieldset className="reglage" disabled={!peutModifier || enCours}>
      <legend>{titre}</legend>
      <p className="muted" style={{ margin: "0 0 0.25rem" }}>{aide}</p>
      {CHOIX.map((c) => (
        <label key={c.valeur} className="reglage__choix">
          <input type="radio" name={reglage} checked={(parametres[reglage] ?? "") === c.valeur}
                 onChange={() => void choisir(reglage, c.valeur)} />
          <span><strong>{c.libelle}</strong><small>{c.aide}</small></span>
        </label>
      ))}
    </fieldset>
  );

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Transmission et nom des signataires</h2>
      <p>Valeurs proposées à chaque nouvelle enveloppe ; la transmission se choisit aussi signataire par
        signataire au moment de créer l'enveloppe.</p>
      {!peutModifier && moi && (
        <p className="muted">Réservé au propriétaire ou à un administrateur du compte.</p>
      )}
      {groupe("transmission_defaut", "Transmettre la signature à une autre personne",
              "Le destinataire indique la personne qui signera à sa place (par exemple, la secrétaire au dirigeant).")}
      {groupe("correction_nom_defaut", "Corriger le nom du signataire (Signature eIDAS Avancée AES)",
              "La pièce d'identité vérifiée ne correspond pas au nom que vous avez indiqué : le document prend le nom vérifié.")}
      {message && <div className="alert alert--success" style={{ marginTop: "0.75rem" }}>{message}</div>}
      <Erreur erreur={erreur} />
    </div>
  );
}

/** Porteur, 12/10/2026 : message propose a chaque destinataire d'une
 *  nouvelle enveloppe, modifiable pour chacun au moment de l'envoi. */
function MessageParDefaut() {
  const [texte, setTexte] = useState<string | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  const [fait, setFait] = useState(false);

  useEffect(() => {
    api.parametresClient().then((p) => setTexte(p.message_defaut ?? "")).catch(setErreur);
  }, []);

  if (texte === null) return <Erreur erreur={erreur} />;

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>Message par défaut aux destinataires</h2>
      <p>Proposé à chaque destinataire d'une nouvelle enveloppe ; vous pouvez le modifier pour chacun.</p>
      <textarea className="input" rows={3} maxLength={2000} value={texte}
                onChange={(e) => { setTexte(e.target.value); setFait(false); }} />
      <div className="actions">
        <button type="button" className="btn btn--primary"
                onClick={() => void api.definirMessageDefaut(texte).then(() => setFait(true), setErreur)}>
          Enregistrer
        </button>
      </div>
      {fait && <div className="alert alert--success" style={{ marginTop: "0.75rem" }}>Message enregistré.</div>}
      <Erreur erreur={erreur} />
    </div>
  );
}

function DureeDesLiens() {
  const [parametres, setParametres] = useState<ParametresClient | null>(null);
  const [saisie, setSaisie] = useState("");
  const [erreur, setErreur] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api
      .parametresClient()
      .then((p) => {
        setParametres(p);
        setSaisie(p.signing_link_days === null ? "" : String(p.signing_link_days));
      })
      .catch(setErreur);
  }, []);

  if (!parametres) return <Erreur erreur={erreur} />;

  const { min, max } = parametres.bornes.signing_link_days;
  const n = Number(saisie);
  const valide = saisie.trim() !== "" && Number.isInteger(n) && n >= min && n <= max;

  const enregistrer = async (jours: number | null) => {
    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      const p = await api.definirDureeLienClient(jours);
      setParametres(p);
      setSaisie(p.signing_link_days === null ? "" : String(p.signing_link_days));
      setMessage(
        p.signing_link_days === null
          ? `Vos liens suivent désormais la valeur de la plateforme (${p.defaut_plateforme} jours).`
          : `Vos prochains liens seront valables ${p.signing_link_days} jours.`,
      );
    } catch (e) {
      setErreur(e);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card__title" style={{ fontSize: "1.1rem" }}>
        Liens de signature
      </h2>
      <p>
        Vos signataires peuvent utiliser leur lien pendant <strong>{parametres.effectif} jours</strong>
        {parametres.signing_link_days === null ? " (valeur de la plateforme)" : " (votre choix)"}. Le
        changement vaut pour les liens envoyés ou renvoyés ensuite ; ceux déjà partis gardent leur
        date d'expiration.
      </p>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (valide && !enCours) void enregistrer(n);
        }}
      >
        <div className="field">
          <label className="field-label" htmlFor="duree-lien">
            Durée en jours (de {min} à {max})
          </label>
          <input
            id="duree-lien"
            className="input"
            type="number"
            min={min}
            max={max}
            step={1}
            placeholder={String(parametres.defaut_plateforme)}
            value={saisie}
            onChange={(e) => {
              setSaisie(e.target.value);
              setMessage(null);
            }}
          />
        </div>
        <div className="actions" style={{ marginTop: 0, alignSelf: "flex-end" }}>
          <button type="submit" className="btn btn--primary" disabled={!valide || enCours}>
            Enregistrer
          </button>
          {parametres.signing_link_days !== null && (
            <button type="button" className="btn" disabled={enCours} onClick={() => void enregistrer(null)}>
              Revenir à la valeur de la plateforme ({parametres.defaut_plateforme} jours)
            </button>
          )}
        </div>
      </form>
      {message && <p className="muted">{message}</p>}
      <Erreur erreur={erreur} />
    </div>
  );
}
